import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import mapValues from "lodash/mapValues";
import { checkIsExpandable } from "PFApp/booking/parts/overview/side_panel_row/side_panel_row.utils";
import {
  BookingModuleStoreType,
  CalendarRootStoreSlice,
  CalendarRootStoreSliceActions,
  ProjectsCounters,
  ProjectsEngagementsActions,
  ProjectsEngagementsSlice
} from "PFApp/booking/store";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { EngagementWallPlannerMode } from "PFApp/booking/types/engagement_wallplanner_mode";
import { StateCreator } from "zustand";

import { useProjectsStoreSlice } from "./use_projects_store_slice";

type ProjectsEngagementsSliceCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  CalendarRootStoreSlice<ProjectsEngagementsSlice, ProjectsCounters>
>;
type ProjectsEngagementsSliceActionsCreator = StateCreator<
  BookingModuleStoreType,
  [],
  [],
  CalendarRootStoreSliceActions<ProjectsCounters, ProjectsEngagementsActions>
>;

type UseProjectsStoreStateReturn = {
  createProjectsEngagementsStoreSlice: ProjectsEngagementsSliceCreator;
  createProjectsEngagementsStoreSliceActions: ProjectsEngagementsSliceActionsCreator;
};

export const useProjectsEngagementsStoreSlice = (): UseProjectsStoreStateReturn => {
  const { createProjectsStoreSlice, createProjectsStoreSliceActions } = useProjectsStoreSlice({
    calendarRoot: CalendarRoot.ProjectsEngagements
  });

  const createProjectsEngagementsStoreSlice: ProjectsEngagementsSliceCreator = (...args) => ({
    ...createProjectsStoreSlice(...args),
    isRoleLevelHidden: false,
    engagementWallplannerMode: EngagementWallPlannerMode.Group
  });

  const createProjectsEngagementsStoreSliceActions: ProjectsEngagementsSliceActionsCreator = (...args) => {
    const [set, get] = args;
    return {
      ...createProjectsStoreSliceActions(...args),
      setIsRoleLevelHidden: (isRoleLevelHidden) => {
        const projectsEngagementsSlice = get()[CalendarRoot.ProjectsEngagements];
        set({ [CalendarRoot.ProjectsEngagements]: { ...projectsEngagementsSlice, isRoleLevelHidden } });
      },
      setEngagementWallplannerMode: (engagementWallplannerMode) => {
        const prevMode = get()[CalendarRoot.ProjectsEngagements].engagementWallplannerMode;
        if (prevMode === engagementWallplannerMode) {
          return;
        }
        const isWrapMode = engagementWallplannerMode === EngagementWallPlannerMode.Wrap;
        const rootRows = get()[CalendarRoot.ProjectsEngagements].rows.filter(({ parents }) =>
          isEmpty(parents)
        );
        // when toggling wrap mode ON, the root rows should be automatically expanded. When OFF - collapsed
        const rootRowsExpandedState = mapValues(keyBy(rootRows, "key"), (row) =>
          isWrapMode ? checkIsExpandable(CalendarRoot.ProjectsEngagements, row, null) : false
        );
        get().actions[CalendarRoot.ProjectsEngagements].setExpandedRowsState({
          ...get()[CalendarRoot.ProjectsEngagements].expandedRowsState,
          ...rootRowsExpandedState
        });
        set({
          [CalendarRoot.ProjectsEngagements]: {
            ...get()[CalendarRoot.ProjectsEngagements],
            engagementWallplannerMode
          }
        });
      }
    };
  };

  return { createProjectsEngagementsStoreSlice, createProjectsEngagementsStoreSliceActions };
};
